import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useSpring, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Reveal } from "@/components/Reveal";
import { useSEO } from "@/components/SEO";
import { CALCULATOR_PRODUCTS, getProduct } from "@/lib/products";
import { inrFull, inrShort } from "@/lib/format";
import { MoneyInput, MoneyWords } from "@/components/MoneyInput";

const AnimatedMoney = ({ value, className, testId }) => {
  const spring = useSpring(value, { stiffness: 110, damping: 24 });
  useEffect(() => {
    spring.set(value);
  }, [value, spring]);
  const text = useTransform(spring, (v) => inrFull(v));
  return <motion.span data-testid={testId} className={className}>{text}</motion.span>;
};

export default function EligibilityPage() {
  const [slug, setSlug] = useState("home-loan");
  const product = getProduct(slug) || CALCULATOR_PRODUCTS[1];
  const [income, setIncome] = useState(150000);
  const [existingEmi, setExistingEmi] = useState(20000);
  const [foir, setFoir] = useState(50);
  const [rate, setRate] = useState(Math.round((product.rateMin + 0.15) * 100) / 100);
  const [months, setMonths] = useState(product.tenureDefault);

  useSEO({
    title: "Loan Eligibility Calculator — How Much Can You Borrow? | Loan Salaah",
    description: "Find out how much you can borrow based on your income and existing EMIs. Instant eligibility estimate for home, personal, business and property loans.",
  });

  const switchProduct = (nextSlug) => {
    const p = getProduct(nextSlug);
    if (!p) return;
    setSlug(nextSlug);
    setRate(Math.round((p.rateMin + 0.15) * 100) / 100);
    setMonths(p.tenureDefault);
  };

  const incomeNum = Number(income) || 0;
  const existingNum = Number(existingEmi) || 0;

  const r = useMemo(() => {
    const maxEmiCapacity = Math.max(0, (incomeNum * foir) / 100);
    const availableEmi = Math.max(0, maxEmiCapacity - existingNum);
    const i = rate / 12 / 100;
    const n = Math.max(1, months);
    let amount;
    if (i === 0) {
      amount = availableEmi * n;
    } else {
      const pow = Math.pow(1 + i, n);
      amount = (availableEmi * (pow - 1)) / (i * pow);
    }
    return { maxEmiCapacity, availableEmi, amount: Math.round(amount) };
  }, [incomeNum, existingNum, foir, rate, months]);

  const applyParams = `/apply?product=${slug}&amount=${r.amount}&rate=${rate}&tenure=${months}&emi=${Math.round(r.availableEmi)}`;

  return (
    <div data-testid="eligibility-page" className="pb-24 pt-36">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl">
          <div className="flex items-center gap-4">
            <span className="brass-rule" aria-hidden="true" />
            <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">Eligibility</span>
          </div>
          <h1 className="mt-5 font-serif text-4xl font-semibold tracking-tight text-concordia sm:text-5xl lg:text-6xl">
            How much can you actually borrow?
          </h1>
          <p className="mt-5 text-base leading-relaxed text-slate">
            Lenders typically let your total EMIs take 40–55% of your net monthly income. Tell us your income and
            current EMIs — we'll estimate your borrowing power instantly, right here on your device.
          </p>
        </Reveal>

        <Reveal delay={0.12} className="mt-12">
          <div className="grid overflow-hidden rounded-3xl border border-sand bg-white shadow-[0_30px_80px_rgba(30,74,61,0.1)] lg:grid-cols-5" data-testid="eligibility-calculator">
            {/* Controls */}
            <div className="p-6 sm:p-10 lg:col-span-3">
              <div className="flex flex-wrap gap-2" role="tablist" aria-label="Loan product">
                {CALCULATOR_PRODUCTS.map((p) => (
                  <button
                    key={p.slug}
                    role="tab"
                    aria-selected={slug === p.slug}
                    data-testid={`eligibility-tab-${p.slug}`}
                    onClick={() => switchProduct(p.slug)}
                    className={`rounded-full px-4 py-2 text-xs font-semibold transition-all duration-300 sm:text-sm ${
                      slug === p.slug
                        ? "bg-concordia text-ivory shadow-[0_8px_20px_rgba(30,74,61,0.3)]"
                        : "border border-sand bg-cream text-charcoal/70 hover:border-concordia/40 hover:text-concordia"
                    }`}
                  >
                    {p.name}
                  </button>
                ))}
              </div>

              <div className="mt-10 space-y-10">
                <div>
                  <div className="mb-3 flex items-end justify-between gap-4">
                    <label htmlFor="eligibility-income-input" className="text-sm font-medium text-slate">Net monthly income</label>
                    <MoneyInput
                      id="eligibility-income-input"
                      testId="eligibility-income-input"
                      wordsTestId="eligibility-income-words"
                      value={income}
                      max={5000000}
                      inputClass="w-24"
                      wordsSuffix="Rupees per month"
                      onChange={(v) => setIncome(v === "" ? "" : Math.min(5000000, v))}
                      onBlur={() => setIncome((a) => Math.min(5000000, Math.max(15000, Number(a) || 15000)))}
                    />
                  </div>
                  <Slider data-testid="eligibility-income-slider" value={[Math.min(1000000, Math.max(15000, incomeNum || 15000))]} min={15000} max={1000000} step={5000}
                    onValueChange={([v]) => setIncome(v)} className="slider-brand" />
                  <div className="mt-2 flex justify-between font-mono text-[0.65rem] uppercase tracking-wider text-slate/70">
                    <span>₹15k</span><span>₹10 L / month</span>
                  </div>
                </div>

                <div>
                  <div className="mb-3 flex items-end justify-between gap-4">
                    <label htmlFor="eligibility-existing-input" className="text-sm font-medium text-slate">Existing EMIs (total / month)</label>
                    <MoneyInput
                      id="eligibility-existing-input"
                      testId="eligibility-existing-input"
                      wordsTestId="eligibility-existing-words"
                      value={existingEmi}
                      max={incomeNum || 500000}
                      inputClass="w-24"
                      wordsSuffix="Rupees per month"
                      onChange={(v) => setExistingEmi(v === "" ? "" : Math.min(incomeNum || 500000, v))}
                      onBlur={() => setExistingEmi((a) => Math.min(incomeNum, Math.max(0, Number(a) || 0)))}
                    />
                  </div>
                  <Slider data-testid="eligibility-existing-slider" value={[Math.min(500000, Math.max(0, existingNum))]} min={0} max={500000} step={2500}
                    onValueChange={([v]) => setExistingEmi(v)} className="slider-brand" />
                  <div className="mt-2 flex justify-between font-mono text-[0.65rem] uppercase tracking-wider text-slate/70">
                    <span>None</span><span>₹5 L</span>
                  </div>
                </div>

                <div>
                  <div className="mb-3 flex items-end justify-between gap-4">
                    <label htmlFor="eligibility-foir-slider" className="text-sm font-medium text-slate">
                      Max share of income for all EMIs
                      <span className="ml-2 font-mono text-[0.62rem] uppercase tracking-wider text-slate/60">banks allow 40–55%</span>
                    </label>
                    <span className="rounded-xl border border-sand bg-cream px-3 py-1.5 font-semibold text-concordia" data-testid="eligibility-foir-value">{foir}%</span>
                  </div>
                  <Slider id="eligibility-foir-slider" data-testid="eligibility-foir-slider" value={[foir]} min={30} max={65} step={5}
                    onValueChange={([v]) => setFoir(v)} className="slider-brand" />
                </div>

                <div className="grid gap-10 sm:grid-cols-2 sm:gap-6">
                  <div>
                    <div className="mb-3 flex items-end justify-between gap-3">
                      <label htmlFor="eligibility-rate-input" className="text-sm font-medium text-slate">Expected rate</label>
                      <div className="flex items-center rounded-xl border border-sand bg-cream px-3 py-1.5 focus-within:border-brass">
                        <input id="eligibility-rate-input" data-testid="eligibility-rate-input" type="number" step="0.05"
                          value={rate} min={product.rateMin} max={product.rateMax}
                          onChange={(e) => setRate(Math.min(product.rateMax, Math.max(product.rateMin, Number(e.target.value) || product.rateMin)))}
                          className="w-16 bg-transparent text-right font-semibold text-concordia outline-none" />
                        <span className="ml-1 font-semibold text-concordia">%</span>
                      </div>
                    </div>
                    <Slider data-testid="eligibility-rate-slider" value={[rate]} min={product.rateMin} max={product.rateMax} step={0.05}
                      onValueChange={([v]) => setRate(v)} className="slider-brand" />
                  </div>
                  <div>
                    <div className="mb-3 flex items-end justify-between gap-3">
                      <label htmlFor="eligibility-tenure-input" className="text-sm font-medium text-slate">Tenure</label>
                      <div className="flex items-center rounded-xl border border-sand bg-cream px-3 py-1.5 focus-within:border-brass">
                        <input id="eligibility-tenure-input" data-testid="eligibility-tenure-input" type="number"
                          value={months} min={product.tenureMin} max={product.tenureMax}
                          onChange={(e) => setMonths(Math.min(product.tenureMax, Math.max(product.tenureMin, Math.round(Number(e.target.value) || product.tenureMin))))}
                          className="w-16 bg-transparent text-right font-semibold text-concordia outline-none" />
                        <span className="ml-1 text-xs font-semibold text-slate">mo</span>
                      </div>
                    </div>
                    <Slider data-testid="eligibility-tenure-slider" value={[months]} min={product.tenureMin} max={product.tenureMax} step={6}
                      onValueChange={([v]) => setMonths(v)} className="slider-brand" />
                  </div>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="relative flex flex-col justify-between bg-concordia-ink p-6 text-ivory sm:p-10 lg:col-span-2">
              <div className="noise-overlay" aria-hidden="true" />
              <div className="relative">
                <p className="font-mono text-[0.62rem] uppercase tracking-[0.3em] text-brass">You can borrow up to</p>
                <AnimatedMoney value={r.amount} testId="eligibility-amount-value"
                  className="mt-3 block text-4xl font-bold tracking-tight text-ivory sm:text-5xl" />
                <MoneyWords value={r.amount} dark testId="eligibility-amount-words" />
                <p className="mt-2 text-xs text-ivory/50">
                  {inrShort(r.amount)} {product.name.toLowerCase()} · {rate}% · {months} months
                </p>

                <dl className="mt-8 space-y-4 text-sm">
                  <div className="flex items-center justify-between border-b border-ivory/10 pb-3">
                    <dt className="text-ivory/60">EMI capacity ({foir}% of income)</dt>
                    <dd className="text-right font-semibold" data-testid="eligibility-max-emi">{inrFull(r.maxEmiCapacity)}
                      <MoneyWords value={r.maxEmiCapacity} suffix="Rupees per month" dark /></dd>
                  </div>
                  <div className="flex items-center justify-between border-b border-ivory/10 pb-3">
                    <dt className="text-ivory/60">Existing EMIs</dt>
                    <dd className="text-right font-semibold" data-testid="eligibility-existing-echo">− {inrFull(existingNum)}
                      <MoneyWords value={existingNum} suffix="Rupees per month" dark /></dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-ivory/60">Available for new EMI</dt>
                    <dd className="text-right font-semibold text-sage" data-testid="eligibility-available-emi">{inrFull(r.availableEmi)}
                      <MoneyWords value={r.availableEmi} suffix="Rupees per month" dark /></dd>
                  </div>
                </dl>

                {r.availableEmi <= 0 && (
                  <p className="mt-4 rounded-xl border border-brass/40 bg-brass/10 px-4 py-3 text-xs leading-relaxed text-ivory/80" data-testid="eligibility-stretch-hint">
                    Your current EMIs already use this share of income. A co-applicant's income or a longer tenure can
                    open up room — talk to us.
                  </p>
                )}
              </div>

              <Link to={applyParams} data-testid="eligibility-apply-button"
                className="group relative mt-10 inline-flex items-center justify-center gap-2 rounded-full bg-ivory px-7 py-4 text-sm font-semibold text-concordia transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_34px_rgba(0,0,0,0.35)]">
                Apply for this amount
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </Reveal>

        <p className="mt-6 max-w-3xl text-xs leading-relaxed text-slate/80">
          Indicative estimate only. Actual eligibility depends on the lender's policy, your credit score, employer or
          business profile, and existing obligations. A Loan Salaah advisor can compute your exact eligibility across
          partner lenders — free.
        </p>
      </div>
    </div>
  );
}
